const getExplicitBoolean = (...values) => values.find((value) => typeof value === 'boolean');

const getFilterSectionConfig = (item = {}, fallbackFilterSection) => {
  const showFilterByValue = getExplicitBoolean(
    item?.showFilterByValue,
    item?.uiConfig?.showFilterByValue,
    item?.tableUtilityConfig?.showFilterByValue,
  );
  const showFilterByCondition = getExplicitBoolean(
    item?.showFilterByCondition,
    item?.uiConfig?.showFilterByCondition,
    item?.tableUtilityConfig?.showFilterByCondition,
  );

  const derivedFilterSection = item?.filterSection
    || (
      showFilterByCondition === true && showFilterByValue === false
        ? 'condition'
        : showFilterByValue === true && showFilterByCondition === false
          ? 'value'
          : fallbackFilterSection
    );

  const derivedOutsideFilterSections = Array.isArray(item?.outsideFilterSections)
    && item.outsideFilterSections.length
    ? item.outsideFilterSections
    : (
      showFilterByCondition === true && showFilterByValue === false
        ? ['condition']
        : showFilterByValue === true && showFilterByCondition === false
          ? ['value']
          : undefined
    );

  return {
    filterSection: derivedFilterSection,
    outsideFilterSections: derivedOutsideFilterSections,
  };
};

const normalizeTableUtilityResponse = (response) => {
  if (!response || typeof response !== 'object') {
    return response;
  }

  // Accept menuItemLabel as a second argument for context
  const addTemplateDefaults = (item = {}, menuItemLabel = '') => {
    if (item?.type !== 'filter') {
      return item;
    }

    // Use menuItem label if present, else item.label, else last part of field, else field, else 'Unknown'
    let labelName = '';
    if (menuItemLabel) {
      labelName = menuItemLabel;
    } else if (item.label) {
      labelName = item.label;
    } else if (item.field) {
      labelName = item.field.split('.').slice(-1)[0] || item.field;
    } else {
      labelName = 'Unknown';
    }

    const filterSectionConfig = getFilterSectionConfig(item);

    const normalizedItem = {
      ...item,
      label: item?.label || (item?.field ? `Filter by ${labelName}` : ''),
      ...(filterSectionConfig.filterSection
        ? { filterSection: filterSectionConfig.filterSection }
        : {}),
      ...(filterSectionConfig.outsideFilterSections
        ? { outsideFilterSections: filterSectionConfig.outsideFilterSections }
        : {}),
    };

    switch (item?.template) {
    case 'multiSelect':
    case 'choice':
      normalizedItem.selectedValues = Array.isArray(item?.selectedValues)
        ? item.selectedValues
        : [];
      break;
    case 'singleSelect':
      normalizedItem.selectedValues = Array.isArray(item?.selectedValues)
        ? item.selectedValues
        : [];
      normalizedItem.selectedValue = item?.selectedValue ?? null;
      normalizedItem.selectedLabel = item?.selectedLabel ?? '';
      break;
    case 'dateRange':
    case 'range':
      normalizedItem.range = Array.isArray(item?.range) && item.range.length >= 2
        ? [item.range[0] ?? null, item.range[1] ?? null]
        : [null, null];
      normalizedItem.selectedValues = Array.isArray(item?.selectedValues)
        ? item.selectedValues
        : [];
      break;
    case 'inputfield':
      normalizedItem.inputValue = item?.inputValue ?? '';
      break;
    case 'valueFilter':
    case 'numberFilter':
      normalizedItem.condition = item?.condition ?? null;
      normalizedItem.value = item?.value ?? null;
      normalizedItem.range = Array.isArray(item?.range) && item.range.length >= 2
        ? [item.range[0] ?? null, item.range[1] ?? null]
        : [null, null];
      break;
    default:
      break;
    }

    return normalizedItem;
  };

  const rawTableConfiguration
    = response.data?.tableConfiguration
    ?? response.data?.data?.tableConfiguration
    ?? response.tableConfiguration
    ?? response.tableConfig
    ?? response.data?.tableConfig
    ?? response.data?.data?.tableConfig
    ?? [];

  const rawTableData
    = response.data?.tableData
    ?? response.data?.data?.tableData
    ?? response.tableData
    ?? response.data?.data
    ?? [];

  const addGlobalFilterFlag = (menuItems = []) => (
    (Array.isArray(menuItems) ? menuItems : []).map((menuItem) => {
      return ({
        ...menuItem,
        items: Array.isArray(menuItem?.items)
          ? menuItem.items.map((item) => {
            const normalizedItem = addTemplateDefaults(item, menuItem.label);
            return {
              ...normalizedItem,
            };
          })
          : menuItem?.items,
      });
    })
  );

  const normalizedColumns = Array.isArray(rawTableConfiguration)
    ? rawTableConfiguration.map((column) => {

      // 1. Ensure filter is strictly boolean and defaults to true if missing
      const isFilterable = typeof column?.filter === 'boolean' ? column.filter : true;

      return ({
        ...column,
        ...(column?.actions ? { actions: column.actions } : {}),
        filter: isFilterable,
        sortable:
              typeof column?.sortable === 'boolean' ? column.sortable : true,
        pinnable:
              typeof column?.pinnable === 'boolean' ? column.pinnable : true,

        // 2. This controls if the filter row is visible by default
        filterVisible:
              typeof column?.filterVisible === 'boolean' ? column.filterVisible : true,

        // 3. This ensures the icon button appears in the header
        showFilterButtonInHeader:
              typeof column?.showFilterButtonInHeader === 'boolean'
                ? column.showFilterButtonInHeader
                : isFilterable, // If it's filterable, show the button

        showPinButtonInHeader:
              typeof column?.showPinButtonInHeader === 'boolean'
                ? column.showPinButtonInHeader
                : Boolean(column?.defaultPinned),
        menuItems: addGlobalFilterFlag(column?.menuItems),
      });
    })
    : [];

  const helperFieldSet = new Set(
    normalizedColumns
      .map((column) => column?.field)
      .filter(Boolean),
  );

  const helperColumns = normalizedColumns.flatMap((column, columnIndex) => (
    (Array.isArray(column?.menuItems) ? column.menuItems : []).flatMap((menuItem) => (
      (Array.isArray(menuItem?.items) ? menuItem.items : []).flatMap((item) => {
        if (item?.type !== 'filter' || !item?.field || item.field === column?.field) {
          return [];
        }

        if (helperFieldSet.has(item.field)) {
          return [];
        }

        helperFieldSet.add(item.field);

        return [{
          field: item.field,
          header: item?.label || menuItem?.label || column?.header || item.field,
          filter: false,
          visible: false,
          defaultPinned: false,
          pinnable: false,
          sortable: false,
          filterVisible: false,
          showFilterButtonInHeader: false,
          showPinButtonInHeader: false,
          // isGlobalFilter: false,
          order: (normalizedColumns.length + 1) + columnIndex,
          menuItems: [{
            label: menuItem?.label || column?.header || item.field,
            // isGlobalFilter: false,
            items: [{
              ...addTemplateDefaults(item, menuItem.label),
              // isGlobalFilter: false,
            }],
          }],
        }];
      })
    ))
  ));

  const tableConfiguration = [...normalizedColumns, ...helperColumns];

  const pagination
    = response.meta
    ?? response.pagination
    ?? response.paginationConfig
    ?? response.data?.meta
    ?? response.data?.pagination
    ?? response.data?.paginationConfig
    ?? response.data?.data?.meta
    ?? response.data?.data?.pagination
    ?? response.data?.data?.paginationConfig
    ?? null;

  response.data = {
    ...(response.data || {}),
    tableConfiguration,
    tableData: rawTableData,
  };

  response.meta = {
    ...(response.meta || {}),
    page: pagination?.page ?? response.meta?.page ?? 1,
    limit: pagination?.limit ?? response.meta?.limit ?? 10,
    total:
      pagination?.total
      ?? pagination?.count
      ?? response.meta?.total
      ?? (Array.isArray(rawTableData) ? rawTableData.length : 0),
  };

  response.data.meta = {
    ...(response.data?.meta || {}),
    ...response.meta,
  };

  if (typeof response.success === 'undefined') {
    response.success = Array.isArray(tableConfiguration) && Array.isArray(rawTableData);
  }

  return response;
};

export default normalizeTableUtilityResponse;
